// create slices
import { createSlice } from "@reduxjs/toolkit";
import { ApiStatus } from "../../../common/services/api-status.enum";

// Upload statuses (lower-cased) at which an ARC archive download is considered settled;
// anything else after a successful download means the engagement is still syncing.
export const ARC_TERMINAL_UPLOAD_STATUSES = new Set(["submitted by arc", "Download Error (ARC)"]);

// A bulk ARC download passes every selected engagement in one comma-separated string;
// split it so each engagement's per-row status is tracked individually.
const parseGuidList = (value) => String(value ?? "").split(",").map((part) => part.trim()).filter(Boolean);

const initialState = {
  utility: {
    utilityData: {
      data: [],
      status: ApiStatus.NONE,
      error: null,
    },
  },
  adminEngagement: {
    adminEngagementData: {
      data: [],
      status: ApiStatus.NONE,
      error: null,
    },
  },
  adminFiles: {
    adminFilesData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  adminOriginalFileNames: {
    adminOriginalFileNamesData: {
      data: {}, // keyed by engagementGUID -> { status, data: array, error }
      status: ApiStatus.NONE,
      error: null,
    }
  },
  filesEngagement: {
    filesEngagementData: { data: [], status: ApiStatus.NONE, error: null },
  },
  partnerFilesBlocklist: {
    partnerFilesBlocklistData: {
      data: [],
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersEditEngagementDetails: {
    partnersEditEngagementDetailsData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersFileValidation: {
    partnersFileValidationData: {
      data: [],
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersFilesDelete: {
    partnersFilesDeleteData: {
      data: [],
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersViewFilesContent: {
    partnersViewFilesContentData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersDeleteTempFilesOnPopupClose: {
    partnersDeleteTempFilesOnPopupCloseData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  partnersDownloadFilesContent: {
    partnersDownloadFilesContentData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  partnersEditEngagementStatus: {
    partnersEditEngagementStatusData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersSasToken: {
    partnersSasTokenData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersUploadProcessing: {
    partnersUploadProcessingData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
   partnersUploadReProcessing: {
    partnersUploadReProcessingData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersUploadSharePointFilesToBlob: {
    partnersUploadSharePointFilesToBlobData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  partnersAccessGrant: {
    partnersAccessGrantData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    },
  },
  partnersAccessRevoke: {
    partnersAccessRevokeData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  partnersUploadCounts: {
    partnersUploadCountsData: {
      // keyed by engagementId_GUID
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  partnersDownloadArcArchives: {
    partnersDownloadArcArchivesData: {
      // keyed by engagementId_GUID -> { status, error }
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  adminDownloadFile: {
    adminDownloadFileData: {
      // keyed by sanitized file name
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  adminExportExcel: {
    adminExportExcelData: {
      data: {},
      status: ApiStatus.NONE,
      error: null,
    }
  },
  adminStartAnonymization: {
    adminStartAnonymizationData: {
      // keyed by engagementGUID -> { status, message, error }
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  // Admin bookmark per file (keyed by controlId)
  adminBookmarkFile: {
    adminBookmarkFileData: {
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  // Admin gold counts (files processed in Gold layer)
  adminGoldCounts: {
    adminGoldCountsData: {
      // keyed by engagementId_GUID -> { totalFiles, goldProcessedFiles }
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  partnersUploadedFileStatus: {
    partnersUploadedFileStatusData: {
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  partnersDuplicateFiles: {
    partnersDuplicateFilesData: {
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  partnersEnableDisableClient: {
    partnersEnableDisableClientData: {
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  partnersErrorSave: {
    partnersErrorSaveData: {
      data: {},
      status: ApiStatus.NONE,
      error: null
    }
  },
  plannedOutageActive: {
    plannedOutageActiveData: {
      data: null,
      status: ApiStatus.NONE,
      error: null,
    },
  },
  plannedOutagePrior: {
    plannedOutagePriorData: {
      data: null,
      status: ApiStatus.NONE,
      error: null
    }
  }
};

const partnerFileSlice = createSlice({
  name: "partnerFileData",
  initialState,
  reducers: {
    //getUtilityData
    getUtilityData: (state) => {
      state.utility.utilityData.status = ApiStatus.LOADING;
    },
    getUtilityDataSuccess: (state, action) => {
      state.utility.utilityData.status = ApiStatus.SUCCESS;
      state.utility.utilityData.data = action.payload;
    },
    getUtilityDataFailure: (state, action) => {
      state.utility.utilityData.status = ApiStatus.ERROR;
      state.utility.utilityData.error = action.payload;
    },
    //getAdminEngagementData
    getAdminEngagementData: (state) => {
      state.adminEngagement.adminEngagementData.status = ApiStatus.LOADING;
    },
    getAdminEngagementDataSuccess: (state, action) => {
      state.adminEngagement.adminEngagementData.status = ApiStatus.SUCCESS;
      state.adminEngagement.adminEngagementData.data = action.payload;
    },
    getAdminEngagementDataFailure: (state, action) => {
      state.adminEngagement.adminEngagementData.status = ApiStatus.ERROR;
      state.adminEngagement.adminEngagementData.error = action.payload;
    },
    //getAdminFilesData
    getAdminFilesData: (state, action) => {
      const engagementGUID = action.payload.engagementGUID;
      if (!state.adminFiles.adminFilesData.data[engagementGUID]) {
        state.adminFiles.adminFilesData.data[engagementGUID] = { status: ApiStatus.LOADING, data: [], error: null };
      } else {
        state.adminFiles.adminFilesData.data[engagementGUID].status = ApiStatus.LOADING;
      }
    },
    getAdminFilesDataSuccess: (state, action) => {
      const { engagementGUID, data } = action.payload;
      state.adminFiles.adminFilesData.data[engagementGUID] = {
        status: ApiStatus.SUCCESS,
        data: data,
        error: null
      };
    },
    getAdminFilesDataFailure: (state, action) => {
      const { engagementGUID, error } = action.payload;
      state.adminFiles.adminFilesData.data[engagementGUID] = {
        status: ApiStatus.ERROR,
        data: [],
        error: error
      };
    },
    //getFilesEngagementData
    getFilesEngagementData: (state) => {
      state.filesEngagement.filesEngagementData.status = ApiStatus.LOADING;
    },
    getFilesEngagementDataSuccess: (state, action) => {
      state.filesEngagement.filesEngagementData.status = ApiStatus.SUCCESS;
      state.filesEngagement.filesEngagementData.data = action.payload;
    },
    getFilesEngagementDataFailure: (state, action) => {
      state.filesEngagement.filesEngagementData.status = ApiStatus.ERROR;
      state.filesEngagement.filesEngagementData.error = action.payload;
    },
    //getPartnerFilesBlocklistData
    getPartnerFilesBlocklistData: (state) => {
      state.partnerFilesBlocklist.partnerFilesBlocklistData.status =
        ApiStatus.LOADING;
    },
    getPartnerFilesBlocklistDataSuccess: (state, action) => {
      state.partnerFilesBlocklist.partnerFilesBlocklistData.status =
        ApiStatus.SUCCESS;
      state.partnerFilesBlocklist.partnerFilesBlocklistData.data =
        action.payload;
    },
    getPartnerFilesBlocklistDataFailure: (state, action) => {
      state.partnerFilesBlocklist.partnerFilesBlocklistData.status =
        ApiStatus.ERROR;
      state.partnerFilesBlocklist.partnerFilesBlocklistData.error =
        action.payload;
    },
    //getPartnersEditEngagementDetails
    getPartnerEditEngagementDetailsUS: (state) => {
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.status =
        ApiStatus.LOADING;
    },
    getPartnerEditEngagementDetailsEU: (state) => {
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.status =
        ApiStatus.LOADING;
    },
    getPartnerEditEngagementDetailsAU: (state) => {
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.status =
        ApiStatus.LOADING;
    },
    getPartnersEditEngagementDetailsSuccess: (state, action) => {
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.status =
        ApiStatus.SUCCESS;
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.data =
        action.payload;
    },
    getPartnersEditEngagementDetailsFailure: (state, action) => {
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.status =
        ApiStatus.ERROR;
      state.partnersEditEngagementDetails.partnersEditEngagementDetailsData.error =
        action.payload;
    },
    //getPartnersFileValidation
    getPartnersFileValidationUS: (state) => {
      state.partnersFileValidation.partnersFileValidationData.status =
        ApiStatus.LOADING;
    },
    getPartnersFileValidationEU: (state) => {
      state.partnersFileValidation.partnersFileValidationData.status =
        ApiStatus.LOADING;
    },
    getPartnersFileValidationAU: (state) => {
      state.partnersFileValidation.partnersFileValidationData.status =
        ApiStatus.LOADING;
    },
    getPartnersFileValidationSuccess: (state, action) => {
      state.partnersFileValidation.partnersFileValidationData.status =
        ApiStatus.SUCCESS;
      state.partnersFileValidation.partnersFileValidationData.data =
        action.payload;
    },
    getPartnersFileValidationFailure: (state, action) => {
      state.partnersFileValidation.partnersFileValidationData.status =
        ApiStatus.ERROR;
      state.partnersFileValidation.partnersFileValidationData.error =
        action.payload;
    },
    //partnersFilesDelete
    getPartnersFilesDeleteUS: (state) => {
      state.partnersFilesDelete.partnersFilesDeleteData.status =
        ApiStatus.LOADING;
    },
    getPartnersFilesDeleteEU: (state) => {
      state.partnersFilesDelete.partnersFilesDeleteData.status =
        ApiStatus.LOADING;
    },
    getPartnersFilesDeleteAU: (state) => {
      state.partnersFilesDelete.partnersFilesDeleteData.status =
        ApiStatus.LOADING;
    },
    getPartnersFilesDeleteSuccess: (state, action) => {
      state.partnersFilesDelete.partnersFilesDeleteData.status =
        ApiStatus.SUCCESS;
      state.partnersFilesDelete.partnersFilesDeleteData.data = action.payload;
    },
    getPartnersFilesDeleteFailure: (state, action) => {
      state.partnersFilesDelete.partnersFilesDeleteData.status =
        ApiStatus.ERROR;
      state.partnersFilesDelete.partnersFilesDeleteData.error = action.payload;
    },
    // partnersViewFilesContent
    getPartnersViewFilesContentUS: (state) => {
      state.partnersViewFilesContent.partnersViewFilesContentData.status =
        ApiStatus.LOADING;
    },
    getPartnersViewFilesContentEU: (state) => {
      state.partnersViewFilesContent.partnersViewFilesContentData.status =
        ApiStatus.LOADING;
    },
    getPartnersViewFilesContentAU: (state) => {
      state.partnersViewFilesContent.partnersViewFilesContentData.status =
        ApiStatus.LOADING;
    },
    getPartnersViewFilesContentSuccess: (state, action) => {
      state.partnersViewFilesContent.partnersViewFilesContentData.status =
        ApiStatus.SUCCESS;
      state.partnersViewFilesContent.partnersViewFilesContentData.data =
        action.payload;
    },
    getPartnersViewFilesContentFailure: (state, action) => {
      state.partnersViewFilesContent.partnersViewFilesContentData.status =
        ApiStatus.ERROR;
      state.partnersViewFilesContent.partnersViewFilesContentData.error =
        action.payload;
    },
    //partnersDeleteTempFilesOnPopupClose
    PartnersDeleteTempFilesOnPopupCloseUS: (state) => {
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.status =
        ApiStatus.LOADING;
    },
     PartnersDeleteTempFilesOnPopupCloseEU: (state) => {
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.status =
        ApiStatus.LOADING;
    },
    PartnersDeleteTempFilesOnPopupCloseAU: (state) => {
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.status =
        ApiStatus.LOADING;
    },
    PartnersDeleteTempFilesOnPopupCloseSuccess: (state, action) => {
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.status =
        ApiStatus.SUCCESS;
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.data =
        action.payload;
    },
    PartnersDeleteTempFilesOnPopupCloseFailure: (state, action) => {
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.status =
        ApiStatus.ERROR;
      state.partnersDeleteTempFilesOnPopupClose.partnersDeleteTempFilesOnPopupCloseData.error =
        action.payload;
    },
    //partnersFilesDownload
    getPartnersFilesDownloadUS: (state) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status =
        ApiStatus.DOWNLOADING;
    },
    getPartnersFilesDownloadEU: (state) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status =
        ApiStatus.DOWNLOADING;
    },
    getPartnersFilesDownloadAU: (state) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status =
        ApiStatus.DOWNLOADING;
    },
    getPartnersFilesDownloadSuccess: (state, action) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status =
        ApiStatus.SUCCESS;
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.data = action.payload;
    },
    getPartnersFilesDownloadFailure: (state, action) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status =
        ApiStatus.ERROR;
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.error = action.payload;
    },
    resetPartnersFilesDownload: (state, action) => {
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.data = {};
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.status = ApiStatus.NONE;
      state.partnersDownloadFilesContent.partnersDownloadFilesContentData.error = null;      
    },
    //partnersEditEngagementStatus
    partnersEditEngagementStatusUS: (state) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status =
        ApiStatus.LOADING;
    },
    partnersEditEngagementStatusEU: (state) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status =
        ApiStatus.LOADING;
    },
    partnersEditEngagementStatusAU: (state) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status =
        ApiStatus.LOADING;
    },
    partnersEditEngagementStatusSuccess: (state, action) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status =
        ApiStatus.SUCCESS;
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.data = action.payload;
    },
    partnersEditEngagementStatusFailure: (state, action) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status =
        ApiStatus.ERROR;
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.error = action.payload;
    },
    resetPartnersEditEngagementStatus: (state, action) => {
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.data = {};
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.status = ApiStatus.NONE;
      state.partnersEditEngagementStatus.partnersEditEngagementStatusData.error = null;
    },
    //getPartnersSasToken
    partnersSasTokenUS: (state) => {
      state.partnersSasToken.partnersSasTokenData.status =
        ApiStatus.LOADING;
    },
    partnersSasTokenEU: (state) => {
      state.partnersSasToken.partnersSasTokenData.status =
        ApiStatus.LOADING;
    },
    partnersSasTokenAU: (state) => {
      state.partnersSasToken.partnersSasTokenData.status =
        ApiStatus.LOADING;
    },
    partnersSasTokenSuccess: (state, action) => {
      state.partnersSasToken.partnersSasTokenData.status =
        ApiStatus.SUCCESS;
      state.partnersSasToken.partnersSasTokenData.data = action.payload;
    },
    partnersSasTokenFailure: (state, action) => {
      state.partnersSasToken.partnersSasTokenData.status =
        ApiStatus.ERROR;
      state.partnersSasToken.partnersSasTokenData.error = action.payload;
    },
    //getPartnersUploadProcessing
    partnersUploadProcessingUS: (state) => {
      state.partnersUploadProcessing.partnersUploadProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadProcessingEU: (state) => {
      state.partnersUploadProcessing.partnersUploadProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadProcessingAU: (state) => {
      state.partnersUploadProcessing.partnersUploadProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadProcessingSuccess: (state, action) => {
      state.partnersUploadProcessing.partnersUploadProcessingData.status =
        ApiStatus.SUCCESS;
      state.partnersUploadProcessing.partnersUploadProcessingData.data = action.payload;
    },
    partnersUploadProcessingFailure: (state, action) => {
      state.partnersUploadProcessing.partnersUploadProcessingData.status =
        ApiStatus.ERROR;
      state.partnersUploadProcessing.partnersUploadProcessingData.error = action.payload;
    },
      partnersUploadReProcessingUS: (state) => {
      state.partnersUploadReProcessing.partnersUploadReProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadReProcessingEU: (state) => {
      state.partnersUploadReProcessing.partnersUploadReProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadReProcessingAU: (state) => {
      state.partnersUploadReProcessing.partnersUploadReProcessingData.status =
        ApiStatus.LOADING;
    },
    partnersUploadReProcessingSuccess: (state, action) => {
      state.partnersUploadReProcessing.partnersUploadReProcessingData.status =
        ApiStatus.SUCCESS;
      state.partnersUploadReProcessing.partnersUploadReProcessingData.data = action.payload;
    },
    partnersUploadReProcessingFailure: (state, action) => {
      state.partnersUploadReProcessing.partnersUploadReProcessingData.status =
        ApiStatus.ERROR;
      state.partnersUploadReProcessing.partnersUploadReProcessingData.error = action.payload;
    },
    //partnersUploadSharePointFilesToBlob
    partnersUploadSharePointFilesToBlobUS: (state) => {
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.status =
        ApiStatus.LOADING;
    },
    partnersUploadSharePointFilesToBlobEU: (state) => {
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.status =
        ApiStatus.LOADING;
    },
    partnersUploadSharePointFilesToBlobAU: (state) => {
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.status =
        ApiStatus.LOADING;
    },
    partnersUploadSharePointFilesToBlobSuccess: (state, action) => {
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.status =
        ApiStatus.SUCCESS;
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.data = action.payload;
    },
    partnersUploadSharePointFilesToBlobFailure: (state, action) => {
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.status =
        ApiStatus.ERROR;
      state.partnersUploadSharePointFilesToBlob.partnersUploadSharePointFilesToBlobData.error = action.payload;
    },
    //partnersAccessGrant
    partnersAccessGrantUS: (state) => {
      state.partnersAccessGrant.partnersAccessGrantData.status =
        ApiStatus.LOADING;
    },
    partnersAccessGrantEU: (state) => {
      state.partnersAccessGrant.partnersAccessGrantData.status =
        ApiStatus.LOADING;
    },
    partnersAccessGrantAU: (state) => {
      state.partnersAccessGrant.partnersAccessGrantData.status =
        ApiStatus.LOADING;
    },
    partnersAccessGrantSuccess: (state, action) => {
      state.partnersAccessGrant.partnersAccessGrantData.status =
        ApiStatus.SUCCESS;
      state.partnersAccessGrant.partnersAccessGrantData.data = action.payload;
    },
    partnersAccessGrantFailure: (state, action) => {
      state.partnersAccessGrant.partnersAccessGrantData.status =
        ApiStatus.ERROR;
      state.partnersAccessGrant.partnersAccessGrantData.error = action.payload;
    },
    //partnersAccessRevoke
    partnersAccessRevokeUS: (state) => {
      state.partnersAccessRevoke.partnersAccessRevokeData.status =
        ApiStatus.LOADING;
    },
    partnersAccessRevokeEU: (state) => {
      state.partnersAccessRevoke.partnersAccessRevokeData.status =
        ApiStatus.LOADING;
    },
    partnersAccessRevokeAU: (state) => {
      state.partnersAccessRevoke.partnersAccessRevokeData.status =
        ApiStatus.LOADING;
    },
    partnersAccessRevokeSuccess: (state, action) => {
      state.partnersAccessRevoke.partnersAccessRevokeData.status =
        ApiStatus.SUCCESS;
      state.partnersAccessRevoke.partnersAccessRevokeData.data = action.payload;
    },
    partnersAccessRevokeFailure: (state, action) => {
      state.partnersAccessRevoke.partnersAccessRevokeData.status =
        ApiStatus.ERROR;
      state.partnersAccessRevoke.partnersAccessRevokeData.error = action.payload;
    },
    // partnersUploadCounts (GetTotalUploadFileCount per engagement)
    partnersUploadCountsRequest: (state) => {
       state.partnersUploadCounts.partnersUploadCountsData.status = ApiStatus.LOADING;
    },
    partnersUploadCountsSuccess: (state, action) => {
      const { engagementId_GUID, totalPendingFile, noOfUploadedFiles, uploaD_STATUS, _version } = action.payload || {};
      if (engagementId_GUID) {
        const existing = state.partnersUploadCounts.partnersUploadCountsData.data[engagementId_GUID];
        const existingVersion = existing?._version || 0;
        if (!existing || (_version || 0) >= existingVersion) {
          state.partnersUploadCounts.partnersUploadCountsData.data[engagementId_GUID] = {
            totalPendingFile,
            noOfUploadedFiles,
            uploaD_STATUS,
            _version,
            _loading: false
          };
        }
      }
      state.partnersUploadCounts.partnersUploadCountsData.status = ApiStatus.SUCCESS;
    },
    // Apply many engagements' counts in one state update (seeded from utility data)
    partnersUploadCountsBulkSuccess: (state, action) => {
      const data = state.partnersUploadCounts.partnersUploadCountsData.data;
      (action.payload || []).forEach(record => {
        const { engagementId_GUID, totalPendingFile, noOfUploadedFiles, uploaD_STATUS, _version } = record || {};
        if (!engagementId_GUID) return;
        const existingVersion = data[engagementId_GUID]?._version || 0;
        if ((_version || 0) >= existingVersion) {
          data[engagementId_GUID] = {
            totalPendingFile,
            noOfUploadedFiles,
            uploaD_STATUS,
            _version,
            _loading: false
          };
        }
      });
      state.partnersUploadCounts.partnersUploadCountsData.status = ApiStatus.SUCCESS;
    },
    partnersUploadCountsFailure: (state, action) => {
      state.partnersUploadCounts.partnersUploadCountsData.status = ApiStatus.ERROR;
      state.partnersUploadCounts.partnersUploadCountsData.error = action.payload;
    },
    partnersUploadCountSingleRequest: (state, action) => {
       const { engagementId_GUID } = action.payload || {};
      if (engagementId_GUID) {
        const existing = state.partnersUploadCounts.partnersUploadCountsData.data[engagementId_GUID];
        state.partnersUploadCounts.partnersUploadCountsData.data[engagementId_GUID] = {
          ...(existing || {}),
          _loading: true
        };
      }
    },
    // Admin file download (area-specific)
    adminDownloadFileRequest: (state, action) => {
      const { fileName } = action.payload || {};
      if (fileName) {
        state.adminDownloadFile.adminDownloadFileData.data[fileName] = {
          status: ApiStatus.LOADING,
          error: null
        };
      }
      state.adminDownloadFile.adminDownloadFileData.status = ApiStatus.LOADING;
    },
    adminDownloadFileSuccess: (state, action) => {
      const { fileName } = action.payload || {};
      if (fileName) {
        state.adminDownloadFile.adminDownloadFileData.data[fileName] = {
          status: ApiStatus.SUCCESS,
          error: null,
          downloadedAt: Date.now()
        };
      }
      state.adminDownloadFile.adminDownloadFileData.status = ApiStatus.SUCCESS;
    },
    adminDownloadFileFailure: (state, action) => {
      const { fileName, error } = action.payload || {};
      if (fileName) {
        state.adminDownloadFile.adminDownloadFileData.data[fileName] = {
          status: ApiStatus.ERROR,
          error: error || 'Download failed'
        };
      }
      state.adminDownloadFile.adminDownloadFileData.status = ApiStatus.ERROR;
      state.adminDownloadFile.adminDownloadFileData.error = error || 'Download failed';
    },
    adminDownloadFileReset: (state) => {
      state.adminDownloadFile.adminDownloadFileData = { data: {}, status: ApiStatus.NONE, error: null };
    },
    // Admin export excel filtered data
    adminExportExcelRequest: (state) => {
      state.adminExportExcel.adminExportExcelData.status = ApiStatus.LOADING;
      state.adminExportExcel.adminExportExcelData.error = null;
    },
    adminExportExcelSuccess: (state, action) => {
      state.adminExportExcel.adminExportExcelData.status = ApiStatus.SUCCESS;
      state.adminExportExcel.adminExportExcelData.data = action.payload || {};
      state.adminExportExcel.adminExportExcelData.error = null;
    },
    adminExportExcelFailure: (state, action) => {
      state.adminExportExcel.adminExportExcelData.status = ApiStatus.ERROR;
      state.adminExportExcel.adminExportExcelData.error = action.payload || 'Export failed';
    },
    adminExportExcelReset: (state) => {
      state.adminExportExcel.adminExportExcelData = { data: {}, status: ApiStatus.NONE, error: null };
    },
    // Admin original file names per engagement
    adminOriginalFileNamesRequest: (state, action) => {
      const { engagementGUID } = action.payload || {};
      if (engagementGUID) {
        const existing = state.adminOriginalFileNames.adminOriginalFileNamesData.data[engagementGUID];
        state.adminOriginalFileNames.adminOriginalFileNamesData.data[engagementGUID] = {
          status: ApiStatus.LOADING,
          data: existing?.data || [],
          error: null
        };
      }
      state.adminOriginalFileNames.adminOriginalFileNamesData.status = ApiStatus.LOADING;
    },
    adminOriginalFileNamesSuccess: (state, action) => {
      const { engagementGUID, data } = action.payload || {};
      if (engagementGUID) {
        state.adminOriginalFileNames.adminOriginalFileNamesData.data[engagementGUID] = {
          status: ApiStatus.SUCCESS,
          data: data || [],
          error: null
        };
      }
      state.adminOriginalFileNames.adminOriginalFileNamesData.status = ApiStatus.SUCCESS;
    },
    adminOriginalFileNamesFailure: (state, action) => {
      const { engagementGUID, error } = action.payload || {};
      if (engagementGUID) {
        state.adminOriginalFileNames.adminOriginalFileNamesData.data[engagementGUID] = {
          status: ApiStatus.ERROR,
          data: [],
          error: error || 'Failed'
        };
      }
      state.adminOriginalFileNames.adminOriginalFileNamesData.status = ApiStatus.ERROR;
      state.adminOriginalFileNames.adminOriginalFileNamesData.error = error || 'Failed';
    },
    // Admin start anonymization per engagement
    adminStartAnonymizationRequest: (state, action) => {
      const { engagementGUID } = action.payload || {};
      if (engagementGUID) {
        const existing = state.adminStartAnonymization.adminStartAnonymizationData.data[engagementGUID];
        state.adminStartAnonymization.adminStartAnonymizationData.data[engagementGUID] = {
          status: ApiStatus.LOADING,
          // Always clear previous message/error when starting again
          message: null,
          error: null
        };
      }
      state.adminStartAnonymization.adminStartAnonymizationData.status = ApiStatus.LOADING;
    },
    adminStartAnonymizationSuccess: (state, action) => {
      const { engagementGUID, message } = action.payload || {};
      if (engagementGUID) {
        state.adminStartAnonymization.adminStartAnonymizationData.data[engagementGUID] = {
          status: ApiStatus.SUCCESS,
          message: message || 'Success',
          error: null
        };
      }
      state.adminStartAnonymization.adminStartAnonymizationData.status = ApiStatus.SUCCESS;
    },
    adminStartAnonymizationFailure: (state, action) => {
      const { engagementGUID, error } = action.payload || {};
      if (engagementGUID) {
        state.adminStartAnonymization.adminStartAnonymizationData.data[engagementGUID] = {
          status: ApiStatus.ERROR,
          message: null,
          error: error || 'Failed'
        };
      }
      state.adminStartAnonymization.adminStartAnonymizationData.status = ApiStatus.ERROR;
      state.adminStartAnonymization.adminStartAnonymizationData.error = error || 'Failed';
    },
    // Admin manual upload (multipart)
    adminUploadFileRequest: (state, action) => {
      const { requestId } = action.payload || {};
      state.adminManualUpload = state.adminManualUpload || { data: {}, status: ApiStatus.NONE, error: null };
      if (requestId) {
        state.adminManualUpload.data[requestId] = { status: ApiStatus.LOADING, error: null };
      }
      state.adminManualUpload.status = ApiStatus.LOADING;
    },
    adminUploadFileSuccess: (state, action) => {
      const { requestId, response } = action.payload || {};
      if (requestId && state.adminManualUpload?.data) {
        state.adminManualUpload.data[requestId] = { status: ApiStatus.SUCCESS, error: null, response };
      }
      state.adminManualUpload.status = ApiStatus.SUCCESS;
    },
    adminUploadFileFailure: (state, action) => {
      const { requestId, error } = action.payload || {};
      if (requestId && state.adminManualUpload?.data) {
        state.adminManualUpload.data[requestId] = { status: ApiStatus.ERROR, error: error || 'Upload failed' };
      }
      state.adminManualUpload.status = ApiStatus.ERROR;
      state.adminManualUpload.error = error || 'Upload failed';
    },
    adminUploadFileReset: (state) => {
      state.adminManualUpload = { data: {}, status: ApiStatus.NONE, error: null };
    },
    // Admin gold counts
    adminGoldCountsRequest: (state) => {
      state.adminGoldCounts.adminGoldCountsData.status = ApiStatus.LOADING;
      state.adminGoldCounts.adminGoldCountsData.error = null;
    },
    adminGoldCountsSuccess: (state, action) => {
      const payload = action.payload;
      const mapped = {};
      const pushItem = (item) => {
        if (!item) return;
        const key = item?.engagementId_GUID || item?.engagementGUID || item?.engagementId;
        if (!key) return;
        mapped[key] = {
          totalFiles: Number(item?.totalFiles ?? 0),
          goldProcessedFiles: Number(item?.goldProcessedFiles ?? 0)
        };
      };
      if (Array.isArray(payload)) {
        payload.forEach(pushItem);
      } else if (typeof payload === 'object' && payload !== null) {
        pushItem(payload);
      }
      state.adminGoldCounts.adminGoldCountsData.data = {
        ...state.adminGoldCounts.adminGoldCountsData.data,
        ...mapped
      };
      state.adminGoldCounts.adminGoldCountsData.status = ApiStatus.SUCCESS;
    },
    adminGoldCountsFailure: (state, action) => {
      state.adminGoldCounts.adminGoldCountsData.status = ApiStatus.ERROR;
      state.adminGoldCounts.adminGoldCountsData.error = action.payload || 'Failed to load gold counts';
    },
    // Admin bookmark file toggle
    adminBookmarkFileRequest: (state, action) => {
      const { controlId, engagementGUID, isBookmarked } = action.payload || {};
      if (controlId !== undefined && controlId !== null) {
        // Capture previous bookmark to allow revert on failure
        let prevBookmarked = undefined;
        if (engagementGUID && state.adminFiles.adminFilesData.data[engagementGUID]) {
          const arr = state.adminFiles.adminFilesData.data[engagementGUID].data || [];
          const idx = arr.findIndex(f => String(f.controlId) === String(controlId));
          if (idx !== -1) {
            prevBookmarked = !!arr[idx].isBookmarked;
            // Optimistically update UI for smooth toggle
            arr[idx] = { ...arr[idx], isBookmarked: !!isBookmarked };
          }
        }
        state.adminBookmarkFile.adminBookmarkFileData.data[String(controlId)] = {
          status: ApiStatus.LOADING,
          error: null,
          prevBookmarked
        };
      }
      state.adminBookmarkFile.adminBookmarkFileData.status = ApiStatus.LOADING;
    },
    adminBookmarkFileSuccess: (state, action) => {
      const { controlId, isBookmarked, engagementGUID } = action.payload || {};
      if (controlId !== undefined && controlId !== null) {
        state.adminBookmarkFile.adminBookmarkFileData.data[String(controlId)] = {
          status: ApiStatus.SUCCESS,
          error: null,
          updatedAt: Date.now(),
          isBookmarked
        };
      }
      // Reflect bookmark change in adminFiles list if present
      if (engagementGUID && state.adminFiles.adminFilesData.data[engagementGUID]) {
        const arr = state.adminFiles.adminFilesData.data[engagementGUID].data || [];
        const idx = arr.findIndex(f => String(f.controlId) === String(controlId));
        if (idx !== -1) {
          arr[idx] = { ...arr[idx], isBookmarked: !!isBookmarked };
        }
      }
      state.adminBookmarkFile.adminBookmarkFileData.status = ApiStatus.SUCCESS;
    },
    adminBookmarkFileFailure: (state, action) => {
      const { controlId, error, engagementGUID } = action.payload || {};
      if (controlId !== undefined && controlId !== null) {
        const entry = state.adminBookmarkFile.adminBookmarkFileData.data[String(controlId)] || {};
        // Revert optimistic update if we have previous value and engagement context
        if (engagementGUID && state.adminFiles.adminFilesData.data[engagementGUID]) {
          const arr = state.adminFiles.adminFilesData.data[engagementGUID].data || [];
          const idx = arr.findIndex(f => String(f.controlId) === String(controlId));
          if (idx !== -1 && entry.prevBookmarked !== undefined) {
            arr[idx] = { ...arr[idx], isBookmarked: !!entry.prevBookmarked };
          }
        }
        state.adminBookmarkFile.adminBookmarkFileData.data[String(controlId)] = {
          status: ApiStatus.ERROR,
          error: error || 'Bookmark failed',
          prevBookmarked: entry.prevBookmarked
        };
      }
      state.adminBookmarkFile.adminBookmarkFileData.status = ApiStatus.ERROR;
      state.adminBookmarkFile.adminBookmarkFileData.error = error || 'Bookmark failed';
    },
    // partnersUploadedFileStatus
    partnersUploadedFileStatusRequestUS: (state, action) => {
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.status = ApiStatus.LOADING;
    },
    partnersUploadedFileStatusRequestEU: (state, action) => {
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.status = ApiStatus.LOADING;
    },
    partnersUploadedFileStatusRequestAU: (state, action) => {
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.status = ApiStatus.LOADING;
    },
    partnersUploadedFileStatusSuccess: (state, action) => {
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.status = ApiStatus.SUCCESS;
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.data = action.payload || {};
    },
    partnersUploadedFileStatusFailure: (state, action) => {
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.status = ApiStatus.ERROR;
      state.partnersUploadedFileStatus.partnersUploadedFileStatusData.error = action.payload;
    },
    // partnersDuplicateFiles
    partnersDuplicateFilesRequestUS: (state, action) => {
      state.partnersDuplicateFiles.partnersDuplicateFilesData.status = ApiStatus.LOADING;
    },
    partnersDuplicateFilesRequestEU: (state, action) => {
      state.partnersDuplicateFiles.partnersDuplicateFilesData.status = ApiStatus.LOADING;
    },
    partnersDuplicateFilesRequestAU: (state, action) => {
      state.partnersDuplicateFiles.partnersDuplicateFilesData.status = ApiStatus.LOADING;
    },
    partnersDuplicateFilesSuccess: (state, action) => {
      state.partnersDuplicateFiles.partnersDuplicateFilesData.status = ApiStatus.SUCCESS;
      state.partnersDuplicateFiles.partnersDuplicateFilesData.data = action.payload || {};
    },
    partnersDuplicateFilesFailure: (state, action) => {
      state.partnersDuplicateFiles.partnersDuplicateFilesData.status = ApiStatus.ERROR;
      state.partnersDuplicateFiles.partnersDuplicateFilesData.error = action.payload;
    },
    // partnersEnableDisableClientData
    partnersEnableDisableClientDataRequestUS: (state, action) => {
      state.partnersEnableDisableClient.partnersEnableDisableClientData.status = ApiStatus.LOADING;
    },
    partnersEnableDisableClientDataRequestEU: (state, action) => {
      state.partnersEnableDisableClient.partnersEnableDisableClientData.status = ApiStatus.LOADING;
    },
    partnersEnableDisableClientDataRequestAU: (state, action) => {
      state.partnersEnableDisableClient.partnersEnableDisableClientData.status = ApiStatus.LOADING;
    },
    partnersEnableDisableClientDataSuccess: (state, action) => {
      state.partnersEnableDisableClient.partnersEnableDisableClientData.status = ApiStatus.SUCCESS;
      state.partnersEnableDisableClient.partnersEnableDisableClientData.data = action.payload || {};
    },
    partnersEnableDisableClientDataFailure: (state, action) => {
      state.partnersEnableDisableClient.partnersEnableDisableClientData.status = ApiStatus.ERROR;
      state.partnersEnableDisableClient.partnersEnableDisableClientData.error = action.payload;
    },
    // PartnersErrorSaveData
    partnersErrorSaveDataRequest: (state) => {
      state.partnersErrorSave.partnersErrorSaveData.status = ApiStatus.LOADING;
    },
    partnersErrorSaveDataSuccess: (state, action) => {
      state.partnersErrorSave.partnersErrorSaveData.status = ApiStatus.SUCCESS;
      state.partnersErrorSave.partnersErrorSaveData.data = action.payload || {};
    },
    partnersErrorSaveDataFailure: (state, action) => {
      state.partnersErrorSave.partnersErrorSaveData.status = ApiStatus.ERROR;
      state.partnersErrorSave.partnersErrorSaveData.error = action.payload;
    },
    // PlannedOutagePrior
    getPlannedOutagePriorRequest: (state) => {
      state.plannedOutagePrior.plannedOutagePriorData.status = ApiStatus.LOADING;
    },
    getPlannedOutagePriorSuccess: (state, action) => {
      state.plannedOutagePrior.plannedOutagePriorData.status = ApiStatus.SUCCESS;
      state.plannedOutagePrior.plannedOutagePriorData.data = action.payload || null;
    },
    getPlannedOutagePriorFailure: (state, action) => {
      state.plannedOutagePrior.plannedOutagePriorData.status = ApiStatus.ERROR;
      state.plannedOutagePrior.plannedOutagePriorData.error = action.payload;
    },
    // PlannedOutageActive
    getPlannedOutageActiveRequest: (state) => {
      state.plannedOutageActive.plannedOutageActiveData.status = ApiStatus.LOADING;
    },
    getPlannedOutageActiveSuccess: (state, action) => {
      state.plannedOutageActive.plannedOutageActiveData.status = ApiStatus.SUCCESS;
      state.plannedOutageActive.plannedOutageActiveData.data = action.payload || null;
    },
    getPlannedOutageActiveFailure: (state, action) => {
      state.plannedOutageActive.plannedOutageActiveData.status = ApiStatus.ERROR;
      state.plannedOutageActive.plannedOutageActiveData.error = action.payload;
    },
    resetPlannedOutageActive: (state) => {
      state.plannedOutageActive.plannedOutageActiveData.status = ApiStatus.NONE;
      state.plannedOutageActive.plannedOutageActiveData.data = null;
      state.plannedOutageActive.plannedOutageActiveData.error = null;
    },
    // ARC archive download (keyed by engagementId_GUID)
    partnersDownloadArcArchivesRequest: (state, action) => {
      const { engagementId_GUID } = action.payload || {};
      parseGuidList(engagementId_GUID).forEach((guid) => {
        state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.data[guid] = {
          status: ApiStatus.LOADING,
          error: null,
        };
      });
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.status = ApiStatus.LOADING;
    },
    partnersDownloadArcArchivesSuccess: (state, action) => {
      const { engagementId_GUID } = action.payload || {};
      parseGuidList(engagementId_GUID).forEach((guid) => {
        state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.data[guid] = {
          status: ApiStatus.SUCCESS,
          error: null,
        };
      });
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.status = ApiStatus.SUCCESS;
    },
    partnersDownloadArcArchivesFailure: (state, action) => {
      const { engagementId_GUID, error } = action.payload || {};
      parseGuidList(engagementId_GUID).forEach((guid) => {
        state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.data[guid] = {
          status: ApiStatus.ERROR,
          error: error || 'Download failed',
        };
      });
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.status = ApiStatus.ERROR;
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.error = error || 'Download failed';
    },
    resetPartnersDownloadArcArchives: (state, action) => {
      const { engagementId_GUID } = action.payload || {};
      const guids = parseGuidList(engagementId_GUID);
      if (guids.length > 0) {
        guids.forEach((guid) => {
          delete state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.data[guid];
        });
      } else {
        state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.data = {};
      }
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.status = ApiStatus.NONE;
      state.partnersDownloadArcArchives.partnersDownloadArcArchivesData.error = null;
    },
  },
});

export const {
  getUtilityData,
  getUtilityDataSuccess,
  getUtilityDataFailure,
  getAdminEngagementData,
  getAdminEngagementDataSuccess,
  getAdminEngagementDataFailure,
  getAdminFilesData,
  getAdminFilesDataSuccess,
  getAdminFilesDataFailure,
  getFilesEngagementData,
  getFilesEngagementDataSuccess,
  getFilesEngagementDataFailure,
  getPartnerFilesBlocklistData,
  getPartnerFilesBlocklistDataSuccess,
  getPartnerFilesBlocklistDataFailure,
  getPartnerEditEngagementDetailsUS,
  getPartnerEditEngagementDetailsEU,
  getPartnerEditEngagementDetailsAU,
  getPartnersEditEngagementDetailsSuccess,
  getPartnersEditEngagementDetailsFailure,
  getPartnersFileValidationUS,
  getPartnersFileValidationEU,
  getPartnersFileValidationAU,
  getPartnersFileValidationSuccess,
  getPartnersFileValidationFailure,
  getPartnersFilesDeleteUS,
  getPartnersFilesDeleteEU,
  getPartnersFilesDeleteAU,
  getPartnersFilesDeleteSuccess,
  getPartnersFilesDeleteFailure,
  getPartnersViewFilesContentUS,
  getPartnersViewFilesContentEU,
  getPartnersViewFilesContentAU,
  getPartnersViewFilesContentSuccess,
  getPartnersViewFilesContentFailure,
  PartnersDeleteTempFilesOnPopupCloseUS,
  PartnersDeleteTempFilesOnPopupCloseEU,
  PartnersDeleteTempFilesOnPopupCloseAU,
  PartnersDeleteTempFilesOnPopupCloseSuccess,
  PartnersDeleteTempFilesOnPopupCloseFailure,
  getPartnersFilesDownloadUS,
  getPartnersFilesDownloadEU,
  getPartnersFilesDownloadAU,
  getPartnersFilesDownloadSuccess,
  getPartnersFilesDownloadFailure,
  resetPartnersFilesDownload,
  partnersEditEngagementStatusUS,
  partnersEditEngagementStatusEU,
  partnersEditEngagementStatusAU,
  partnersEditEngagementStatusSuccess,
  partnersEditEngagementStatusFailure,
  resetPartnersEditEngagementStatus,
  partnersSasTokenUS,
  partnersSasTokenEU,
  partnersSasTokenAU,
  partnersSasTokenSuccess,
  partnersSasTokenFailure,
  partnersUploadProcessingUS,
  partnersUploadProcessingEU,
  partnersUploadProcessingAU,
  partnersUploadProcessingSuccess,
  partnersUploadProcessingFailure,
   partnersUploadReProcessingUS,
  partnersUploadReProcessingEU,
  partnersUploadReProcessingAU,
  partnersUploadReProcessingSuccess,
  partnersUploadReProcessingFailure,
  partnersUploadSharePointFilesToBlobUS,
  partnersUploadSharePointFilesToBlobEU,
  partnersUploadSharePointFilesToBlobAU,
  partnersUploadSharePointFilesToBlobSuccess,
  partnersUploadSharePointFilesToBlobFailure,
  partnersAccessGrantUS,
  partnersAccessGrantEU,
  partnersAccessGrantAU,
  partnersAccessGrantSuccess,
  partnersAccessGrantFailure,
  partnersAccessRevokeUS,
  partnersAccessRevokeEU,
  partnersAccessRevokeAU,
  partnersAccessRevokeSuccess,
  partnersAccessRevokeFailure,
  partnersUploadCountsRequest,
  partnersUploadCountsSuccess,
  partnersUploadCountsBulkSuccess,
  partnersUploadCountsFailure
  ,partnersUploadCountSingleRequest
  ,adminDownloadFileRequest
  ,adminDownloadFileSuccess
  ,adminDownloadFileFailure
  ,adminDownloadFileReset
  ,adminExportExcelRequest
  ,adminExportExcelSuccess
  ,adminExportExcelFailure
  ,adminExportExcelReset
  ,adminOriginalFileNamesRequest
  ,adminOriginalFileNamesSuccess
  ,adminOriginalFileNamesFailure
  ,adminStartAnonymizationRequest
  ,adminStartAnonymizationSuccess
  ,adminStartAnonymizationFailure
  ,adminUploadFileRequest
  ,adminUploadFileSuccess
  ,adminUploadFileFailure
  ,adminUploadFileReset
  ,adminGoldCountsRequest
  ,adminGoldCountsSuccess
  ,adminGoldCountsFailure
  ,adminBookmarkFileRequest
  ,adminBookmarkFileSuccess
  ,adminBookmarkFileFailure,
  partnersUploadedFileStatusRequestUS,
  partnersUploadedFileStatusRequestEU,
  partnersUploadedFileStatusRequestAU,
  partnersUploadedFileStatusSuccess,
  partnersUploadedFileStatusFailure,
  partnersDuplicateFilesRequestUS,
  partnersDuplicateFilesRequestEU,
  partnersDuplicateFilesRequestAU,
  partnersDuplicateFilesSuccess,
  partnersDuplicateFilesFailure,
  partnersEnableDisableClientDataRequestUS,
  partnersEnableDisableClientDataRequestEU,
  partnersEnableDisableClientDataRequestAU,
  partnersEnableDisableClientDataSuccess,
  partnersEnableDisableClientDataFailure,
  partnersErrorSaveDataRequest,
  partnersErrorSaveDataSuccess,
  partnersErrorSaveDataFailure,
  getPlannedOutagePriorRequest,
  getPlannedOutagePriorSuccess,
  getPlannedOutagePriorFailure,
  getPlannedOutageActiveRequest,
  getPlannedOutageActiveSuccess,
  getPlannedOutageActiveFailure,
  resetPlannedOutageActive,
  partnersDownloadArcArchivesRequest,
  partnersDownloadArcArchivesSuccess,
  partnersDownloadArcArchivesFailure,
  resetPartnersDownloadArcArchives,
} = partnerFileSlice.actions;
export default partnerFileSlice.reducer;
